import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

import API from '../../../data/api';
import CustomModal from '../../custom/CustomModal';
import { MODAL_WIDTH } from '../../../utils/constants';

const initialState = {
  loading: false,
  submitting: false,
  templateDownloading: false,
  depots: [],
  products: [],
  rows: [],
  startDate: '',
  endDate: '',
  alert: '',
  alertSeverity: 'error',
};

const toNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = String(value).trim().replace(/\s/g, '');
  if (!raw) {
    return null;
  }

  let normalized = raw;
  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.replace(/,/g, '');
  } else if (normalized.includes(',') && /,\d{1,4}$/.test(normalized)) {
    normalized = normalized.replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeKey = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const detectDelimiter = (content = '') => {
  const firstLine = String(content).split(/\r?\n/, 1)[0] || '';
  const commaCount = (firstLine.match(/,/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

const parseDelimitedText = (content = '') => {
  const text = String(content).replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(text);
  const rows = [];
  let row = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      row.push(current);
      current = '';
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }
      row.push(current);
      rows.push(row);
      row = [];
      current = '';
      continue;
    }

    current += char;
  }

  if (current !== '' || row.length > 0) {
    row.push(current);
    rows.push(row);
  }

  return rows;
};

const toIsoDate = (value = '') => {
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : '';
};

const getDownloadFilename = (headers = {}) => {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || '';
  const utfNameMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utfNameMatch?.[1]) {
    return decodeURIComponent(utfNameMatch[1]);
  }
  const plainNameMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (plainNameMatch?.[1]) {
    return plainNameMatch[1];
  }
  return `bulk-pricing-template-${new Date().toISOString().slice(0, 10)}.csv`;
};

const mapDepotToRow = (depot, products = []) => {
  const row = {
    depotId: depot?.depot_id || depot?.id || '',
    depotName: depot?.depot_name || depot?.name || '-',
    selected: true,
    prices: products.reduce((acc, product) => {
      acc[String(product.id)] = '';
      return acc;
    }, {}),
  };

  products.forEach((product) => {
    const existingPrice = (depot?.prices || []).find(
      (priceRow) =>
        String(priceRow?.product_id || '') === String(product?.id)
        || String(priceRow?.product_name || '').toLowerCase() === String(product?.name || '').toLowerCase(),
    );
    const sellPrice = existingPrice?.price_details?.sell_price;
    row.prices[String(product.id)] = sellPrice ?? '';
  });

  return row;
};

export default function BulkDepotPricingModal({ openModal, toggleModalState }) {
  const [state, setState] = useState(initialState);
  const templateUploadRef = useRef(null);

  const selectedRowsCount = useMemo(
    () => state.rows.filter((row) => row.selected).length,
    [state.rows],
  );
  const summaryCards = useMemo(
    () => [
      {
        key: 'depots',
        label: 'Depots loaded',
        value: state.depots.length,
        icon: ApartmentOutlinedIcon,
        iconColor: '#1d4ed8',
        surface: '#eff6ff',
      },
      {
        key: 'products',
        label: 'Products loaded',
        value: state.products.length,
        icon: Inventory2OutlinedIcon,
        iconColor: '#0369a1',
        surface: '#f0f9ff',
      },
      {
        key: 'selected',
        label: 'Selected depots',
        value: selectedRowsCount,
        icon: TaskAltOutlinedIcon,
        iconColor: '#166534',
        surface: '#f0fdf4',
      },
    ],
    [selectedRowsCount, state.depots.length, state.products.length],
  );

  useEffect(() => {
    if (!openModal) {
      return;
    }

    const fetchDepots = async () => {
      setState((prev) => ({ ...prev, loading: true, alert: '', alertSeverity: 'error' }));
      try {
        const [depotsResponse, productsResponse] = await Promise.all([
          API.get('inventory/backoffice/depots?depot_type=pricing'),
          API.get('inventory/backoffice/products'),
        ]);
        const depots = depotsResponse?.status === 200 ? depotsResponse?.data?.depots || [] : [];
        const products = productsResponse?.status === 200 ? productsResponse?.data?.products || [] : [];
        const rows = depots.map((depot) => mapDepotToRow(depot, products));

        let alert = '';
        let alertSeverity = 'error';
        if (products.length === 0) {
          alert = 'No products available for bulk pricing update.';
          alertSeverity = 'warning';
        } else if (rows.length === 0) {
          alert = 'No depots available for pricing update.';
          alertSeverity = 'warning';
        }

        setState((prev) => ({
          ...prev,
          loading: false,
          depots,
          products,
          rows,
          alert,
          alertSeverity,
        }));
      } catch (error) {
        console.error('Error fetching depots for bulk pricing:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          depots: [],
          products: [],
          rows: [],
          alert: 'Unable to load depots/products for bulk pricing.',
          alertSeverity: 'error',
        }));
      }
    };

    fetchDepots();
  }, [openModal]);

  const handleClose = () => {
    setState(initialState);
    toggleModalState('bulkPrice', false);
  };

  const handleRowSelect = (depotId, selected) => {
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (
        row.depotId === depotId
          ? { ...row, selected }
          : row
      )),
    }));
  };

  const handleSelectAll = (selected) => {
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => ({ ...row, selected })),
    }));
  };

  const handlePriceChange = (depotId, productId, value) => {
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (
        row.depotId === depotId
          ? {
              ...row,
              prices: {
                ...row.prices,
                [String(productId)]: value,
              },
            }
          : row
      )),
      alert: '',
      alertSeverity: 'error',
    }));
  };

  const setDateField = (field, value) => {
    setState((prev) => ({
      ...prev,
      [field]: value,
      alert: '',
      alertSeverity: 'error',
    }));
  };

  const triggerTemplateUpload = () => {
    templateUploadRef.current?.click();
  };

  const downloadTemplate = () => {
    const fetchTemplate = async () => {
      setState((prev) => ({
        ...prev,
        templateDownloading: true,
        alert: '',
        alertSeverity: 'error',
      }));
      try {
        const response = await API.get('inventorybackoffice/bulk_pricing_template', {
          responseType: 'blob',
        });
        const blob = response?.data ? new Blob([response.data]) : null;
        if (!blob || blob.size === 0) {
          throw new Error('Template file is empty');
        }
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = getDownloadFilename(response?.headers || {});
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        setState((prev) => ({
          ...prev,
          templateDownloading: false,
          alert: 'Template downloaded. Fill it and upload the same file format to apply updates.',
          alertSeverity: 'success',
        }));
      } catch (error) {
        console.error('Error downloading bulk pricing template:', error);
        setState((prev) => ({
          ...prev,
          templateDownloading: false,
          alert: 'Unable to download pricing template. Please retry.',
          alertSeverity: 'error',
        }));
      }
    };

    fetchTemplate();
  };

  const uploadTemplate = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = String(file.name.split('.').pop() || '').toLowerCase();
    const resetFileInput = () => {
      if (templateUploadRef.current) {
        templateUploadRef.current.value = '';
      }
    };

    if (extension === 'xls' || extension === 'xlsx') {
      setState((prev) => ({
        ...prev,
        alert: 'Excel files are supported for upload selection, but parsing currently requires CSV format.',
        alertSeverity: 'warning',
      }));
      resetFileInput();
      return;
    }

    if (extension !== 'csv') {
      setState((prev) => ({
        ...prev,
        alert: 'Upload a CSV template file.',
        alertSeverity: 'error',
      }));
      resetFileInput();
      return;
    }

    try {
      const fileContent = await file.text();
      const parsedRows = parseDelimitedText(fileContent)
        .filter((row) => row.some((cell) => String(cell || '').trim() !== ''));

      if (parsedRows.length < 2) {
        setState((prev) => ({
          ...prev,
          alert: 'Template file is empty. Download the template and try again.',
          alertSeverity: 'error',
        }));
        resetFileInput();
        return;
      }

      const headers = parsedRows[0].map((header) => String(header || '').trim());
      const headerKeys = headers.map((header) => normalizeKey(header));

      const depotIdIndex = headerKeys.findIndex((key) => key === 'depot_id');
      const depotNameIndex = headerKeys.findIndex((key) => key === 'depot_name');
      const startDateIndex = headerKeys.findIndex((key) => key === 'start_date');
      const endDateIndex = headerKeys.findIndex((key) => key === 'end_date');

      if (depotIdIndex < 0 && depotNameIndex < 0) {
        setState((prev) => ({
          ...prev,
          alert: 'Template must include depot_id or depot_name column.',
          alertSeverity: 'error',
        }));
        resetFileInput();
        return;
      }

      const productColumnById = new Map();
      headers.forEach((header, index) => {
        const idMatch = String(header).match(/id\s*:\s*(\d+)/i);
        if (idMatch?.[1]) {
          productColumnById.set(String(idMatch[1]), index);
        }
      });

      state.products.forEach((product) => {
        const productId = String(product.id);
        if (productColumnById.has(productId)) {
          return;
        }
        const candidateIndex = headerKeys.findIndex((key) =>
          key === normalizeKey(product.name)
          || key === normalizeKey(`${product.name}_price`)
          || key === normalizeKey(`price_${product.name}`));
        if (candidateIndex >= 0) {
          productColumnById.set(productId, candidateIndex);
        }
      });

      if (productColumnById.size === 0) {
        setState((prev) => ({
          ...prev,
          alert: 'No product price columns were found in the uploaded template.',
          alertSeverity: 'error',
        }));
        resetFileInput();
        return;
      }

      const rowsByDepotId = new Map(state.rows.map((row) => [String(row.depotId), row]));
      const rowsByDepotName = new Map(
        state.rows.map((row) => [normalizeKey(row.depotName), row]),
      );

      const rowUpdates = new Map();
      let matchedDepots = 0;
      let importedPrices = 0;
      let importedStartDate = '';
      let importedEndDate = '';

      parsedRows.slice(1).forEach((record) => {
        const getCell = (index) => (index >= 0 ? String(record[index] ?? '').trim() : '');
        const depotIdCell = getCell(depotIdIndex);
        const depotNameCell = getCell(depotNameIndex);

        let targetRow = depotIdCell ? rowsByDepotId.get(String(depotIdCell)) : null;
        if (!targetRow && depotNameCell) {
          targetRow = rowsByDepotName.get(normalizeKey(depotNameCell));
        }
        if (!targetRow) {
          return;
        }

        const update = rowUpdates.get(targetRow.depotId) || {
          depotId: targetRow.depotId,
          prices: { ...targetRow.prices },
        };

        productColumnById.forEach((columnIndex, productId) => {
          const rawValue = getCell(columnIndex);
          if (rawValue === '') {
            return;
          }
          const numericPrice = toNumber(rawValue);
          if (numericPrice === null) {
            return;
          }
          update.prices[String(productId)] = String(numericPrice);
          importedPrices += 1;
        });

        if (!rowUpdates.has(targetRow.depotId)) {
          matchedDepots += 1;
        }
        rowUpdates.set(targetRow.depotId, update);

        if (!importedStartDate) {
          importedStartDate = toIsoDate(getCell(startDateIndex));
        }
        if (!importedEndDate) {
          importedEndDate = toIsoDate(getCell(endDateIndex));
        }
      });

      if (matchedDepots === 0 || importedPrices === 0) {
        setState((prev) => ({
          ...prev,
          alert: 'No matching depot prices were imported from the file.',
          alertSeverity: 'warning',
        }));
        resetFileInput();
        return;
      }

      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((row) => {
          const update = rowUpdates.get(row.depotId);
          if (!update) {
            return { ...row, selected: false };
          }
          return {
            ...row,
            selected: true,
            prices: update.prices,
          };
        }),
        startDate: importedStartDate || prev.startDate,
        endDate: importedEndDate || prev.endDate,
        alert: `Imported ${importedPrices} price value(s) across ${matchedDepots} depot(s).`,
        alertSeverity: 'success',
      }));
    } catch (error) {
      console.error('Error importing bulk pricing template:', error);
      setState((prev) => ({
        ...prev,
        alert: 'Unable to parse the template file. Use the downloaded template and upload as CSV.',
        alertSeverity: 'error',
      }));
    } finally {
      resetFileInput();
    }
  };

  const submitBulkPricing = async (event) => {
    event.preventDefault();

    const { rows, startDate, endDate, products } = state;
    if (!startDate || !endDate) {
      setState((prev) => ({
        ...prev,
        alert: 'Start date and end date are required.',
        alertSeverity: 'error',
      }));
      return;
    }
    if (!products.length) {
      setState((prev) => ({
        ...prev,
        alert: 'No products available for bulk pricing update.',
        alertSeverity: 'error',
      }));
      return;
    }

    const selectedRows = rows.filter((row) => row.selected);
    if (selectedRows.length === 0) {
      setState((prev) => ({
        ...prev,
        alert: 'Select at least one depot.',
        alertSeverity: 'error',
      }));
      return;
    }

    const payload = selectedRows
      .map((row) => {
        const items = products
          .map((product) => {
            const numericPrice = toNumber(row?.prices?.[String(product.id)]);
            if (numericPrice === null) {
              return null;
            }
            return {
              product: product.name,
              price: numericPrice,
            };
          })
          .filter(Boolean);

        return {
          depot_id: row.depotId,
          start_date: startDate,
          end_date: endDate,
          items,
        };
      })
      .filter((row) => row.items.length > 0);

    if (payload.length === 0) {
      setState((prev) => ({
        ...prev,
        alert: 'Provide at least one price for selected depots.',
        alertSeverity: 'error',
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      submitting: true,
      alert: '',
      alertSeverity: 'error',
    }));
    try {
      const response = await API.post('inventorybackoffice/bulk_enter_selling_price', payload);
      if (response?.status === 200 || response?.status === 201) {
        handleClose();
        return;
      }
      setState((prev) => ({
        ...prev,
        submitting: false,
        alert: 'Bulk pricing update failed. Please retry.',
        alertSeverity: 'error',
      }));
    } catch (error) {
      console.error('Error submitting bulk pricing:', error);
      setState((prev) => ({
        ...prev,
        submitting: false,
        alert: 'Bulk pricing update failed. Please retry.',
        alertSeverity: 'error',
      }));
    }
  };

  return (
    <CustomModal
      openModal={openModal}
      handleCloseModal={handleClose}
      modalStyle={{ width: { ...MODAL_WIDTH.large } }}
      modalTitle="Bulk Depot Pricing"
      modalIcon="currency"
    >
      <form onSubmit={submitBulkPricing} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Update selling prices for multiple depots in one request.
          </Typography>
          <input
            ref={templateUploadRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            hidden
            onChange={uploadTemplate}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={state.startDate}
              onChange={(event) => setDateField('startDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={state.endDate}
              onChange={(event) => setDateField('endDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Stack>

          <Stack spacing={1}>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              {summaryCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Paper
                    key={`bulk-pricing-summary-${card.key}`}
                    sx={{
                      px: 1.25,
                      py: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: card.surface,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon fontSize="small" sx={{ color: card.iconColor }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                          {card.label}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {card.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Box>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={state.rows.length > 0 && selectedRowsCount === state.rows.length}
                  indeterminate={selectedRowsCount > 0 && selectedRowsCount < state.rows.length}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                />
              )}
              label="Select all depots"
            />
          </Stack>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Apply</TableCell>
                  <TableCell>Depot</TableCell>
                  {state.products.map((product) => (
                    <TableCell key={`bulk-pricing-col-${product.id}`} align="right">
                      {product.name} Price
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.rows.map((row) => (
                  <TableRow key={`bulk-price-row-${row.depotId}`} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={row.selected}
                        onChange={(event) => handleRowSelect(row.depotId, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{row.depotName}</TableCell>
                    {state.products.map((product) => (
                      <TableCell key={`bulk-pricing-cell-${row.depotId}-${product.id}`} align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row?.prices?.[String(product.id)] ?? ''}
                          onChange={(event) => handlePriceChange(row.depotId, product.id, event.target.value)}
                          disabled={!row.selected}
                          inputProps={{ step: '0.01', min: 0 }}
                          sx={{ maxWidth: 130 }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {state.rows.length === 0 && !state.loading && (
                  <TableRow>
                    <TableCell colSpan={2 + state.products.length} align="center">
                      No depots available.
                    </TableCell>
                  </TableRow>
                )}
                {state.loading && (
                  <TableRow>
                    <TableCell colSpan={2 + state.products.length} align="center">
                      Loading depots...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 1.2,
              borderStyle: 'dashed',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-start' } }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileOutlinedIcon />}
                  onClick={triggerTemplateUpload}
                  disabled
                  sx={{ minWidth: { md: 190 } }}
                >
                  Upload pricing file
                </Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <LoadingButton
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={downloadTemplate}
                  loading={state.templateDownloading}
                  disabled={state.loading}
                  sx={{ minWidth: { md: 190 } }}
                >
                  Download template
                </LoadingButton>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: { xs: 'left', md: 'right' } }}
              >
                Upload pricing file is temporarily disabled. You can still download the template.
              </Typography>
            </Box>
          </Paper>

          {state.alert && (
            <Alert severity={state.alertSeverity} variant="outlined">
              {state.alert}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button color="inherit" variant="contained" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={state.submitting}
              disabled={state.loading || state.products.length === 0}
            >
              Submit Bulk Update
            </LoadingButton>
          </Box>
        </Stack>
      </form>
    </CustomModal>
  );
}

BulkDepotPricingModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  toggleModalState: PropTypes.func.isRequired,
};
